/**
 * ActivePosition Component
 * 
 * Optimized version with:
 * - Simplified fetch logic to prevent perpetual loading
 * - Component-level timeout protection
 * - Proper cleanup on unmount
 * - Streamlined state management
 */

import { 
  memo, 
  useState, 
  useEffect, 
  useCallback, 
  useRef, 
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMobile } from '@/hooks/useResponsiveDesign';
import { Button } from '@/components/ui/Button';
import { RefreshCw } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { toastError, toastSuccess } from '@/components/ui/use-toast';
import { ConfirmationModal } from '@/components/ui/ConfirmationModal';
import { formatNumber, formatCurrency } from '@/lib/utils';
import { useLoadingConsolidation } from '@/utils/cache';
import { authenticatedFetch } from '@/utils/authFetch';
import { API_BASE_URL } from '@/config';
import { paperTradingApi } from '@/services/paperTradingApi';
import type { DashboardMarketPrice } from '@/api/trading.service';
import type { PositionUpdateEvent } from '@/services/websocketService';
import type { 
  Position, 
  PositionsState, 
  PositionsSource, 
  PositionsUpdateStrategy,
  TpSlNormalizedPosition,
  PositionPnL
} from './types';
import TakeProfitStopLossModal from '../TakeProfitStopLossModal';

// Constants
const ACTIVE_POSITIONS_FETCH_TIMEOUT_MS = 8000;
const POSITIONS_AUTO_REFRESH_INTERVAL_MS = 60000;

interface ActivePositionProps {
  isPaperMode?: boolean;
  isModeResolved?: boolean;
  modeRequestVersion?: number;
  isRealtimeConnected?: boolean;
  dashboardMarketPrice?: DashboardMarketPrice | null;
  initialData?: any;
  onClosePosition?: (positionId: string) => void;
  positions?: Position[];
  refreshTrigger?: number;
  onPositionsChange?: (positions: Position[]) => void;
  onDataRefresh?: () => Promise<void>;
  onPositionRefresh?: () => void;
  onModeChange?: () => void;
  onLoaded?: () => void;
  onError?: (error: string) => void;
  onUnrealizedPnLChange?: (pnl: number) => void;
  onWebSocketPositionUpdate?: (handler: (event: PositionUpdateEvent) => void) => (() => void);
}

export const ActivePosition = memo(function ActivePosition({
  isPaperMode = false,
  isModeResolved = true,
  modeRequestVersion = 0,
  isRealtimeConnected = false,
  dashboardMarketPrice = null,
  initialData = null,
  onClosePosition,
  positions: initialPositions = [],
  refreshTrigger,
  onPositionsChange,
  onDataRefresh,
  onPositionRefresh,
  onModeChange,
  onLoaded,
  onError,
  onUnrealizedPnLChange,
  onWebSocketPositionUpdate,
}: ActivePositionProps) {
  const { t } = useTranslation();
  const { isMobile } = useMobile();
  const { withLoadingConsolidation } = useLoadingConsolidation();
  
  // Core state
  const [positionsState, setPositions] = useState<PositionsState>(() => ({
    positions: Array.isArray(initialPositions) ? initialPositions : [],
    app_positions: [],
    external_positions: [],
    source: isPaperMode ? 'paper' : 'all',
    isPaperMode
  }));
  
  const positions = positionsState.positions;
  
  // Publish the active position snapshot to the parent (used by the chart overlays).
  const onPositionsChangeRef = useRef(onPositionsChange);
  
  useEffect(() => {
    onPositionsChangeRef.current = onPositionsChange;
  }, [onPositionsChange]);
  
  useEffect(() => {
    onPositionsChangeRef.current?.(positions);
  }, [positions]);
  
  useEffect(() => {
    return () => {
      onPositionsChangeRef.current?.([]);
    };
  }, []);
  
  // Loading and error states with timeout protection
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'app' | 'external'>(() => (isPaperMode ? 'all' : 'all'));
  
  // Modal and position management state
  const [selectedPosition, setSelectedPosition] = useState<Position | null>(null);
  const [normalizedTpSlPosition, setNormalizedTpSlPosition] = useState<TpSlNormalizedPosition | null>(null);
  const [tpSlModalOpen, setTpSlModalOpen] = useState(false);
  const [closingPosition, setClosingPosition] = useState<string | null>(null);
  const [isCloseConfirmModalOpen, setIsCloseConfirmModalOpen] = useState(false);
  const [positionToClose, setPositionToClose] = useState<Position | null>(null);
  
  // Paper trading settings
  const [paperInvestmentPercentage, setPaperInvestmentPercentage] = useState<number | null>(null);
  
  // Refs for tracking state without triggering re-renders
  const isMountedRef = useRef(true);
  const hasLoadedRef = useRef(false);
  const skipInitialFetchRef = useRef(false);
  const fetchAbortControllerRef = useRef<AbortController | null>(null);
  const fetchGenerationRef = useRef(0);
  const modeVersionRef = useRef(modeRequestVersion);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Derived values from props
  const canonicalBtcMarkPrice = useMemo(() => {
    if (!dashboardMarketPrice) return null;
    return dashboardMarketPrice.mark_price ?? dashboardMarketPrice.markPrice ?? dashboardMarketPrice.price ?? null;
  }, [dashboardMarketPrice]);
  
  const priceTick = useMemo(
    () => dashboardMarketPrice?.timestamp ?? canonicalBtcMarkPrice ?? 0,
    [dashboardMarketPrice?.timestamp, canonicalBtcMarkPrice]
  );
  
  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      fetchGenerationRef.current += 1;
      fetchAbortControllerRef.current?.abort();
      fetchAbortControllerRef.current = null;
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);
  
  // Update mode version ref
  useEffect(() => {
    modeVersionRef.current = modeRequestVersion;
  }, [modeRequestVersion]);
  
  // Update mode and reset state when paper mode changes
  useEffect(() => {
    if (!isPaperMode) {
      setPaperInvestmentPercentage(null);
    }
    
    setPositions(prev => ({ ...prev, isPaperMode }));
  }, [isPaperMode]);
  
  // Paper trading settings loader
  useEffect(() => {
    if (!isPaperMode) return;
    
    let isMounted = true;
    
    const loadPaperSettings = async () => {
      try {
        const response = await paperTradingApi.getPaperRiskSettings();
        if (!isMounted || !response?.success) return;
        
        const pct = response.investment_percentage;
        setPaperInvestmentPercentage(typeof pct === 'number' ? pct : null);
      } catch (error) {
        console.error('[ActivePosition] Failed to fetch paper risk settings:', error);
      }
    };
    
    loadPaperSettings();
    
    const handleSettingsUpdate = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (!detail) return;
      
      setPaperInvestmentPercentage(typeof detail.investmentPercentage === 'number' ? detail.investmentPercentage : null);
    };
    
    window.addEventListener('paperRiskSettingsUpdated', handleSettingsUpdate);
    
    return () => {
      isMounted = false;
      window.removeEventListener('paperRiskSettingsUpdated', handleSettingsUpdate);
    };
  }, [isPaperMode]);
  
  // Helper: Safe parse float
  const safeParseFloat = useCallback((value: any, defaultValue: number | undefined = undefined): number | undefined => {
    if (value === null || value === undefined || value === '') return defaultValue;
    const parsed = parseFloat(value);
    return isNaN(parsed) || parsed <= 0 ? defaultValue : parsed;
  }, []);
  
  // Helper: Parse number allowing negative values
  const parseNumberAllowSign = useCallback((value: any): number | undefined => {
    if (value === null || value === undefined || value === '') return undefined;
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }, []);
  
  // Helper: Resolve position amounts
  const resolvePositionAmounts = useCallback((position: Position, entryPriceHint?: number) => {
    const baseQtyCandidates = [
      position.positionAmt,
      position.quantity,
      (position as any).qty,
      (position as any).base_quantity,
      (position as any).baseQuantity,
      (position as any).baseQty,
      (position as any).amount,
      position.size,
    ];

    let baseQty: number | undefined;
    for (const candidate of baseQtyCandidates) {
      const parsed = parseNumberAllowSign(candidate);
      if (parsed !== undefined) {
        baseQty = parsed;
        break;
      }
    }

    const entryPrice = entryPriceHint ?? safeParseFloat(position.entryPrice ?? position.entry_price);

    let notional = safeParseFloat(
      (position as any).notional ??
      (position as any).position_notional ??
      (position as any).size_usd
    );

    if (notional !== undefined) {
      notional = Math.abs(notional);
    }

    if ((baseQty === undefined || baseQty === 0) && notional !== undefined && entryPrice !== undefined && entryPrice > 0) {
      baseQty = notional / entryPrice;
    }

    if ((notional === undefined || notional === 0) && baseQty !== undefined && entryPrice !== undefined) {
      notional = Math.abs(baseQty * entryPrice);
    }

    return { baseQty, notional, entryPrice };
  }, [parseNumberAllowSign, safeParseFloat]);
  
  // Helper: Determine if position is long
  const isPositionActuallyLong = useCallback((position: Position): boolean => {
    if (!position) return true;
    const side = position.side?.toUpperCase();
    if (side === 'LONG') return true;
    if (side === 'SHORT') return false;
    
    // Try to infer from PnL behavior
    if (position.markPrice && position.entryPrice) {
      const pnl = position.pnl || 0;
      const priceWentUp = position.markPrice > position.entryPrice;
      const pnlIsPositive = pnl > 0;
      
      if (priceWentUp && pnlIsPositive) return true;
      if (priceWentUp && !pnlIsPositive) return false;
      if (!priceWentUp && pnlIsPositive) return false;
      if (!priceWentUp && !pnlIsPositive) return true;
    }
    
    return side === 'BUY';
  }, []);
  
  // Helper: Calculate position PnL
  const calculatePositionPNL = useCallback((position: Position): PositionPnL => {
    const entry = safeParseFloat(position.entryPrice ?? position.entry_price);
    const { baseQty: qty } = resolvePositionAmounts(position, entry);
    const leverageValue = safeParseFloat(position.leverage ?? (position as any).leverageMultiplier);
    
    const symbolUpper = position.symbol?.toUpperCase() ?? '';
    const isBtc = symbolUpper === 'BTCUSDT' || symbolUpper.startsWith('BTC');
    let currentPrice: number | undefined;
    
    if (isBtc && canonicalBtcMarkPrice) {
      currentPrice = canonicalBtcMarkPrice;
    } else {
      currentPrice = safeParseFloat(position.markPrice ?? position.mark_price ?? position.current_price);
    }
    
    if (entry === undefined || qty === undefined || currentPrice === undefined) {
      // Fallback to server-provided PnL
      const serverPnl = position.unrealized_pnl ?? position.pnl;
      if (typeof serverPnl === 'number') {
        return { pnl: serverPnl, percentage: 0, roe: 0 };
      }
      return { pnl: 0, percentage: 0, roe: 0 };
    }
    
    const isLong = isPositionActuallyLong(position);
    const absQty = Math.abs(qty);
    const entryValue = absQty * entry;
    const currentValue = absQty * currentPrice;
    const pnl = isLong ? (currentValue - entryValue) : (entryValue - currentValue);
    
    // Calculate margin basis for ROI
    let marginBasis: number | undefined;
    const explicitMargin = safeParseFloat(
      position.margin_used ?? (position as any).marginUsed ?? (position as any).isolatedWallet
    );
    
    if (isPaperMode && entryValue > 0 && leverageValue && leverageValue > 0) {
      marginBasis = entryValue / leverageValue;
    } else if (explicitMargin !== undefined) {
      marginBasis = explicitMargin;
    }
    
    const roi = marginBasis && marginBasis > 0 ? (pnl / marginBasis) * 100 : 0;
    
    return { pnl, percentage: roi, roe: roi, isolatedMargin: marginBasis };
  }, [canonicalBtcMarkPrice, isPaperMode, isPositionActuallyLong, resolvePositionAmounts, safeParseFloat]);
  
  // Memoized positions with PnL
  const positionsWithPnl = useMemo(() => 
    positions?.map((p: Position) => ({ position: p, pnl: calculatePositionPNL(p) })) || [],
    [positions, calculatePositionPNL, priceTick]
  );
  
  // Helper: Get position merge key
  const getPositionMergeKey = useCallback((position: Position | null | undefined): string => {
    return String(
      position?.position_key
      ?? position?.paper_trade_id
      ?? position?.paperTradeId
      ?? (position as any)?.trade_id
      ?? (position as any)?.tradeId
      ?? position?.id
      ?? ''
    );
  }, []);
  
  // Helper: Merge position lists
  const mergePositionList = useCallback((previous: Position[] = [], incoming: Position[] = []): Position[] => {
    if (incoming.length === 0) return incoming;
    
    const previousByKey = new Map<string, Position>();
    previous.forEach((position) => {
      const key = getPositionMergeKey(position);
      if (key) {
        previousByKey.set(key, position);
      }
    });
    
    return incoming.map((position) => {
      const key = getPositionMergeKey(position);
      if (!key) return position;
      const prev = previousByKey.get(key);
      if (!prev) return position;
      return { ...prev, ...position };
    });
  }, [getPositionMergeKey]);
  
  // Helper: Update positions state
  const updatePositionsState = useCallback((
    rawData: any,
    extracted: Position[],
    source: PositionsSource,
    strategy: PositionsUpdateStrategy = 'replace',
  ) => {
    const nextDiagnostics = rawData?.diagnostics ?? (
      rawData && (rawData.position_incidents || rawData.exchange_sync_status)
        ? {
            position_incidents: rawData.position_incidents ?? [],
            exchange_sync_status: rawData.exchange_sync_status ?? { healthy: true, incident_count: 0, incidents: [] },
          }
        : undefined
    );

    setPositions((prev) => {
      if (source === 'paper') {
        const nextPaperPositions = strategy === 'merge'
          ? mergePositionList(prev.positions || [], extracted)
          : extracted;
        return {
          positions: nextPaperPositions,
          app_positions: [],
          external_positions: [],
          diagnostics: nextDiagnostics ?? prev.diagnostics,
          source,
          isPaperMode,
        };
      }

      const explicitAppPositions = Array.isArray(rawData?.app_positions)
        ? rawData.app_positions
        : Array.isArray((rawData as any)?.appPositions)
          ? (rawData as any).appPositions
          : undefined;
      const explicitExternalPositions = Array.isArray(rawData?.external_positions)
        ? rawData.external_positions
        : Array.isArray((rawData as any)?.externalPositions)
          ? (rawData as any).externalPositions
          : undefined;

      const inferredAppPositions = extracted.filter((p: any) => {
        const origin = (p?.source || p?.position_origin || '').toLowerCase();
        return origin !== 'external' && origin !== 'paper' && !p?.paper_trade_id && !p?.paperTradeId;
      });
      const inferredExternalPositions = extracted.filter((p: any) => 
        (p?.source || p?.position_origin || '').toLowerCase() === 'external'
      );

      const nextAppSeed = explicitAppPositions !== undefined
        ? explicitAppPositions
        : source === 'app'
          ? extracted
          : source === 'all'
            ? (strategy === 'merge' && inferredAppPositions.length === 0 ? (prev.app_positions || []) : inferredAppPositions)
            : (prev.app_positions || []);

      const nextExternalSeed = explicitExternalPositions !== undefined
        ? explicitExternalPositions
        : source === 'external'
          ? extracted
          : source === 'all'
            ? (strategy === 'merge' && inferredExternalPositions.length === 0 ? (prev.external_positions || []) : inferredExternalPositions)
            : (prev.external_positions || []);

      const nextAppPositions = strategy === 'merge'
        ? mergePositionList(prev.app_positions || [], nextAppSeed)
        : nextAppSeed;
      const nextExternalPositions = strategy === 'merge'
        ? mergePositionList(prev.external_positions || [], nextExternalSeed)
        : nextExternalSeed;

      const nextVisiblePositions = source === 'app'
        ? nextAppPositions
        : source === 'external'
          ? nextExternalPositions
          : [...nextAppPositions, ...nextExternalPositions];

      return {
        positions: nextVisiblePositions,
        app_positions: nextAppPositions ?? [],
        external_positions: nextExternalPositions ?? [],
        diagnostics: nextDiagnostics ?? prev.diagnostics,
        source,
        isPaperMode,
      };
    });
  }, [isPaperMode, mergePositionList]);
  
  // Validate positions data
  const isValidPositionsData = useCallback((data: any): boolean => {
    if (!data || typeof data !== 'object') return false;
    if (isPaperMode) {
      return Array.isArray(data.positions);
    }
    return (
      Array.isArray(data.positions) ||
      Array.isArray(data.app_positions) ||
      Array.isArray((data as any)?.appPositions) ||
      Array.isArray(data.external_positions) ||
      Array.isArray((data as any)?.externalPositions)
    );
  }, [isPaperMode]);
  
  // Extract positions from data
  const extractPositionsFromData = useCallback((data: any): Position[] => {
    if (!data) return [];
    if (isPaperMode) {
      return Array.isArray(data.positions) ? data.positions : [];
    }

    const appPositions = Array.isArray(data.app_positions || data.appPositions) 
      ? (data.app_positions || data.appPositions) 
      : [];
    const externalPositions = Array.isArray(data.external_positions || data.externalPositions) 
      ? (data.external_positions || data.externalPositions) 
      : [];

    if (appPositions.length > 0 || externalPositions.length > 0) {
      return [...appPositions, ...externalPositions];
    }

    if (Array.isArray(data.positions)) {
      return data.positions;
    }
    return [...appPositions, ...externalPositions];
  }, [isPaperMode]);

  useEffect(() => {
    if (!isModeResolved || !isValidPositionsData(initialData)) {
      return;
    }

    const extracted = extractPositionsFromData(initialData);
    const source: PositionsSource = isPaperMode ? 'paper' : 'all';
    updatePositionsState(initialData, extracted, source);
    hasLoadedRef.current = true;
    skipInitialFetchRef.current = true;
    setLoading(false);
    setError(null);
    onLoaded?.();
  }, [extractPositionsFromData, initialData, isModeResolved, isPaperMode, isValidPositionsData, onLoaded, updatePositionsState]);
  
  // Fetch positions with timeout protection
  const fetchPositions = useCallback(async (
    source: PositionsSource = 'external', 
    options?: { forceRefresh?: boolean }
  ) => {
    if (!isModeResolved || !isMountedRef.current) {
      setLoading(false);
      return;
    }
    
    // Set loading state and start timeout protection
    setLoading(true);
    setError(null);
    
    // Clear any existing timeout
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }
    
    // Set a safety timeout to prevent perpetual loading
    loadingTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current && loading) {
        console.warn('[ActivePosition] Fetch timeout - forcing loading state to false');
        setLoading(false);
        setError('Request timed out. Please try refreshing.');
      }
    }, ACTIVE_POSITIONS_FETCH_TIMEOUT_MS + 2000);
    
    try {
      let data: any;
      
      if (isPaperMode) {
        // Paper mode: fetch from paper trading API
        fetchAbortControllerRef.current?.abort();
        const paperPositions = await withLoadingConsolidation(
          options?.forceRefresh
            ? `paperPositions:${fetchGenerationRef.current}:fresh`
            : `paperPositions:${fetchGenerationRef.current}`,
          () => paperTradingApi.getPaperPositions({ forceRefresh: true })
        );
        
        if (!isMountedRef.current) return;
        
        if (!paperPositions?.success) {
          throw new Error('Failed to fetch paper positions');
        }
        
        data = {
          positions: paperPositions.positions || [],
          source: 'paper',
          isPaperMode: true
        };
      } else {
        // Live mode: fetch from trading API
        const forceRefreshQuery = options?.forceRefresh ? '&force_refresh=1' : '';
        const result = await withLoadingConsolidation(
          options?.forceRefresh
            ? `livePositions:${source}:${fetchGenerationRef.current}:fresh`
            : `livePositions:${source}:${fetchGenerationRef.current}`,
          async () => {
            fetchAbortControllerRef.current?.abort();
            const controller = new AbortController();
            fetchAbortControllerRef.current = controller;
            const response = await authenticatedFetch(
              `${API_BASE_URL}/api/trading/active_position?source=${source}&fast=1${forceRefreshQuery}`,
              { signal: controller.signal },
            );
            const payload = await response.json().catch((jsonError: unknown) => {
              if ((jsonError as { name?: string } | null)?.name === 'AbortError') {
                throw jsonError;
              }
              return null;
            });
            return { response, payload };
          },
        );
        
        if (!isMountedRef.current) return;
        
        if (result.response.ok) {
          data = result.payload;
        } else {
          const errorData: any = result.payload ?? {};
          const errorText = String(errorData?.error || '').toLowerCase();
          const missingCred = (
            result.response.status === 400 || result.response.status === 401 ||
            errorData?.code === 'missing_credentials' ||
            errorText.includes('no valid api credentials') ||
            errorText.includes('no active api credentials')
          );
          
          if (missingCred) {
            setError(t('dashboard.activePositions.noApiCredentials'));
          } else {
            setError(errorData?.error || t('dashboard.activePositions.errorLoading'));
          }
          console.error('ActivePosition fetch failed:', result.response.status, errorData);
          setLoading(false);
          return;
        }
      }
      
      if (!isMountedRef.current) return;
      
      if (!isValidPositionsData(data)) {
        throw new Error('Invalid positions structure received');
      }
      
      const extracted = extractPositionsFromData(data);
      updatePositionsState(data, extracted, isPaperMode ? 'paper' : source);
      
      setError(null);
      hasLoadedRef.current = true;
      
      // Call onLoaded callback
      onLoaded?.();
    } catch (e: any) {
      if (!isMountedRef.current) return;
      
      if (e?.name === 'AbortError') {
        // Request was aborted, don't update state
        return;
      }
      
      if (e?.response?.status === 401) {
        setError(t('errors.unauthorized'));
      } else {
        setError(e?.message || t('dashboard.activePositions.errorLoading'));
      }
      
      console.error('ActivePosition fetch error:', e);
      onError?.(e?.message || t('dashboard.activePositions.errorLoading'));
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }
      }
      fetchAbortControllerRef.current = null;
    }
  }, [isModeResolved, isPaperMode, isValidPositionsData, extractPositionsFromData, updatePositionsState, withLoadingConsolidation, onLoaded, onError, t]);
  
  // Initial fetch and refresh trigger handling
  useEffect(() => {
    if (!isModeResolved) return;

    if (skipInitialFetchRef.current) {
      skipInitialFetchRef.current = false;
      return;
    }
    
    // Always fetch on mount or when mode/refreshTrigger changes
    fetchPositions(activeTab, { forceRefresh: !hasLoadedRef.current });
  }, [isModeResolved, isPaperMode, activeTab, refreshTrigger, fetchPositions]);
  
  // Auto-refresh interval (only when not using WebSocket)
  useEffect(() => {
    if (!isModeResolved || isRealtimeConnected) return;
    
    const interval = setInterval(() => {
      if (!loading) {
        fetchPositions(activeTab);
      }
    }, POSITIONS_AUTO_REFRESH_INTERVAL_MS);
    
    return () => clearInterval(interval);
  }, [isModeResolved, isRealtimeConnected, loading, activeTab, fetchPositions]);
  
  // Paper trading reset event handler
  useEffect(() => {
    const handlePaperTradingReset = () => {
      if (isPaperMode) {
        console.log('[ActivePosition] Reset event detected, clearing positions');
        updatePositionsState({ positions: [], source: 'paper', isPaperMode: true }, [], 'paper');
        setError(null);
        
        setTimeout(() => {
          fetchPositions(activeTab);
        }, 300);
      }
    };
    
    window.addEventListener('paperTradingReset', handlePaperTradingReset);
    return () => window.removeEventListener('paperTradingReset', handlePaperTradingReset);
  }, [isPaperMode, activeTab, fetchPositions, updatePositionsState]);
  
  // WebSocket position update handler
  useEffect(() => {
    if (!onWebSocketPositionUpdate) return;
    
    const unregister = onWebSocketPositionUpdate((event) => {
      if (!isMountedRef.current) return;
      
      const data = event;
      const hasValidStructure = isValidPositionsData(data);
      const extractedPositions = hasValidStructure ? extractPositionsFromData(data) : [];
      
      if (!hasValidStructure) return;
      
      // Update positions with merge strategy for real-time updates
      const source = isPaperMode ? 'paper' : 'all';
      updatePositionsState(data, extractedPositions, source, 'merge');
      
      setError(null);
    });
    
    return () => unregister?.();
  }, [onWebSocketPositionUpdate, isPaperMode, isValidPositionsData, extractPositionsFromData, updatePositionsState]);
  
  // Modal control functions
  const handleOpenCloseConfirmModal = useCallback((position: Position) => {
    setPositionToClose(position);
    setIsCloseConfirmModalOpen(true);
  }, []);
  
  const handleCloseConfirmModal = useCallback(() => {
    setIsCloseConfirmModalOpen(false);
    setPositionToClose(null);
  }, []);
  
  // TP/SL validation
  const validatePositionForTpSl = useCallback((position: Position) => {
    const errors: string[] = [];
    
    if (!position || typeof position !== 'object') {
      errors.push('Position object is missing or invalid');
      return { isValid: false, errors, normalized: undefined as TpSlNormalizedPosition | undefined };
    }
    
    const symbol = typeof position.symbol === 'string' ? position.symbol.trim() : '';
    if (!symbol) errors.push('Position symbol is missing or invalid');
    
    const side = typeof position.side === 'string' ? position.side.trim() : '';
    if (!side) errors.push('Position side is missing or invalid');
    
    const entryPrice = safeParseFloat(position.entryPrice ?? position.entry_price);
    if (entryPrice === undefined) errors.push('Position entry price is missing or invalid');
    
    const size = safeParseFloat(position.size ?? position.quantity);
    if (size === undefined) errors.push('Position size is missing or invalid');
    
    const id = position.id || position.paper_trade_id || '';
    if (!id) errors.push('Position ID is missing');
    
    const markPrice = safeParseFloat(position.markPrice ?? position.mark_price);
    
    const normalized = symbol && side && entryPrice !== undefined && size !== undefined && id
      ? { symbol, side, entryPrice, size, ...(markPrice !== undefined ? { markPrice } : {}), id }
      : undefined;
    
    return { isValid: errors.length === 0, errors, normalized: errors.length === 0 ? normalized : undefined };
  }, [safeParseFloat]);
  
  // Open TP/SL modal
  const handleOpenTpSlModal = useCallback((position: Position) => {
    const validation = validatePositionForTpSl(position);
    
    if (!validation.isValid || !validation.normalized) {
      toastError({
        title: t('dashboard.activePositions.errorInvalidPositionData'),
        description: validation.errors.join('. ')
      });
      return;
    }
    
    setSelectedPosition(position);
    setNormalizedTpSlPosition(validation.normalized);
    setTpSlModalOpen(true);
  }, [validatePositionForTpSl, t]);
  
  // Set take profit
  const handleSetTakeProfit = useCallback(async (price: number) => {
    if (!selectedPosition) return;
    
    const positionId = selectedPosition.paper_trade_id || selectedPosition.id;
    if (!positionId) {
      toastError({
        title: t('dashboard.activePositions.errorInvalidPosition'),
        description: t('dashboard.activePositions.errorInvalidPositionDesc')
      });
      return;
    }
    
    try {
      let result;
      if (isPaperMode) {
        result = await paperTradingApi.setTakeProfit(positionId, price);
      } else {
        const { setTakeProfit } = await import('@/services/api');
        result = await setTakeProfit(positionId, price);
      }
      
      if (result.success) {
        toastSuccess({
          title: t('dashboard.activePositions.takeProfitSet'),
          description: t('dashboard.activePositions.takeProfitSetDesc', { price: price.toFixed(2) })
        });
        fetchPositions(activeTab, { forceRefresh: true });
      } else {
        throw new Error('Failed to set Take Profit');
      }
    } catch (error) {
      console.error('[ActivePositions] Error setting take profit:', error);
      toastError({
        title: t('dashboard.activePositions.errorSettingTakeProfit'),
        description: error instanceof Error ? error.message : t('dashboard.activePositions.errorSettingTakeProfitDesc')
      });
    }
  }, [selectedPosition, isPaperMode, activeTab, fetchPositions, t]);
  
  // Set stop loss
  const handleSetStopLoss = useCallback(async (price: number) => {
    if (!selectedPosition) return;
    
    const positionId = selectedPosition.paper_trade_id || selectedPosition.id;
    if (!positionId) {
      toastError({
        title: t('dashboard.activePositions.errorInvalidPosition'),
        description: t('dashboard.activePositions.errorInvalidPositionDesc')
      });
      return;
    }
    
    try {
      let result;
      if (isPaperMode) {
        result = await paperTradingApi.setStopLoss(positionId, price);
      } else {
        const { setStopLoss } = await import('@/services/api');
        result = await setStopLoss(positionId, price);
      }
      
      if (result.success) {
        toastSuccess({
          title: t('dashboard.activePositions.stopLossSet'),
          description: t('dashboard.activePositions.stopLossSetDesc', { price: price.toFixed(2) })
        });
        fetchPositions(activeTab, { forceRefresh: true });
      } else {
        throw new Error('Failed to set Stop Loss');
      }
    } catch (error) {
      console.error('[ActivePositions] Error setting stop loss:', error);
      toastError({
        title: t('dashboard.activePositions.errorSettingStopLoss'),
        description: error instanceof Error ? error.message : t('dashboard.activePositions.errorSettingStopLossDesc')
      });
    }
  }, [selectedPosition, isPaperMode, activeTab, fetchPositions, t]);
  
  // Close position
  const handleClosePosition = useCallback(async (positionId: string) => {
    if (onClosePosition) {
      await onClosePosition(positionId);
      return;
    }
    
    const positionIdStr = String(positionId);
    
    try {
      setClosingPosition(positionIdStr);
      
      let result: any;
      if (isPaperMode) {
        result = await paperTradingApi.closePaperPosition(positionIdStr, 'manual');
      } else {
        const { closeLivePosition } = await import('@/services/api');
        result = await closeLivePosition(positionIdStr);
      }
      
      if (result?.success) {
        // Remove position from local state
        setPositions((prev) => {
          const getId = (p: any) => String(p?.paper_trade_id ?? p?.paperTradeId ?? p?.trade_id ?? p?.tradeId ?? p?.id ?? '');
          const matches = (p: any) => {
            const id = getId(p);
            return !id || id !== positionIdStr;
          };
          
          return {
            ...prev,
            positions: Array.isArray(prev.positions) ? prev.positions.filter(matches) : [],
            app_positions: Array.isArray(prev.app_positions) ? prev.app_positions.filter(matches) : prev.app_positions,
            external_positions: Array.isArray(prev.external_positions) ? prev.external_positions.filter(matches) : prev.external_positions,
          };
        });
        
        const pnlValue = typeof result.pnl === 'number' ? result.pnl : result?.pnl?.amount;
        toastSuccess({
          title: t('dashboard.activePositions.positionClosed'),
          description: pnlValue !== undefined
            ? t('dashboard.activePositions.positionClosedDesc', { pnl: pnlValue })
            : t('dashboard.activePositions.positionClosedGeneric')
        });
        
        onDataRefresh?.();
        onPositionRefresh?.();
        onModeChange?.();
        window.dispatchEvent(new CustomEvent('positionRefresh'));
      } else {
        throw new Error('Failed to close position');
      }
    } catch (error) {
      console.error('[ActivePositions] Error closing position:', error);
      toastError({
        title: t('dashboard.activePositions.errorClosingPosition'),
        description: error instanceof Error ? error.message : t('dashboard.activePositions.errorClosingPositionDesc')
      });
    } finally {
      setClosingPosition(null);
    }
  }, [onClosePosition, isPaperMode, onDataRefresh, onPositionRefresh, onModeChange, t]);
  
  // Confirm close position
  const handleConfirmClosePosition = useCallback(() => {
    if (!positionToClose) return;
    const positionId = positionToClose.paper_trade_id || positionToClose.id;
    if (!positionId) {
      toastError({
        title: t('dashboard.activePositions.errorInvalidPosition'),
        description: t('dashboard.activePositions.errorInvalidPositionDesc')
      });
      handleCloseConfirmModal();
      return;
    }
    handleClosePosition(String(positionId));
    handleCloseConfirmModal();
  }, [positionToClose, handleClosePosition, handleCloseConfirmModal, t]);
  
  // Render helper: get TP/SL capability
  const getTpSlCapability = useCallback((position: Position): 'app_managed' | 'exchange_native' | 'read_only' => {
    const capability = (position as any)?.tp_sl_capability;
    if (capability === 'app_managed' || capability === 'exchange_native' || capability === 'read_only') {
      return capability;
    }
    return isPaperMode ? 'app_managed' : ((position?.source || '').toLowerCase() === 'external' ? 'exchange_native' : 'app_managed');
  }, [isPaperMode]);
  
  // Calculate derived position lists
  const paperPositionsWithPnl = useMemo(() =>
    isPaperMode ? positionsWithPnl : [],
    [isPaperMode, positionsWithPnl]
  );
  
  const appPositionsWithPnl = useMemo(() =>
    !isPaperMode 
      ? (positionsState.app_positions?.map((p: Position) => ({ position: p, pnl: calculatePositionPNL(p) })) || [])
      : [],
    [isPaperMode, positionsState.app_positions, calculatePositionPNL]
  );
  
  const externalPositionsWithPnl = useMemo(() =>
    !isPaperMode 
      ? (positionsState.external_positions?.map((p: Position) => ({ position: p, pnl: calculatePositionPNL(p) })) || [])
      : [],
    [isPaperMode, positionsState.external_positions, calculatePositionPNL]
  );

  const accountWideUnrealizedPnL = useMemo(() => {
    const rows = isPaperMode
      ? paperPositionsWithPnl
      : [...appPositionsWithPnl, ...externalPositionsWithPnl];
    return rows.reduce((total, { pnl }) => total + (Number.isFinite(pnl.pnl) ? pnl.pnl : 0), 0);
  }, [appPositionsWithPnl, externalPositionsWithPnl, isPaperMode, paperPositionsWithPnl]);

  useEffect(() => {
    onUnrealizedPnLChange?.(accountWideUnrealizedPnL);
  }, [accountWideUnrealizedPnL, onUnrealizedPnLChange]);

  useEffect(() => () => {
    onUnrealizedPnLChange?.(0);
  }, [onUnrealizedPnLChange]);

  const exposureWarning = useMemo(() => {
    if (isPaperMode || externalPositionsWithPnl.length === 0) {
      return null;
    }

    return {
      title: t('dashboard.activePositions.exposureWarningTitle'),
      description: appPositionsWithPnl.length > 0
        ? t('dashboard.activePositions.exposureWarningMixedDesc')
        : t('dashboard.activePositions.exposureWarningExternalOnlyDesc'),
    };
  }, [appPositionsWithPnl.length, externalPositionsWithPnl.length, isPaperMode, t]);
  
  // Determine if we should show blocking loader
  const hasRenderablePositions = useMemo(() => {
    if (isPaperMode) {
      return paperPositionsWithPnl.length > 0;
    }
    if (activeTab === 'all') {
      return appPositionsWithPnl.length > 0 || externalPositionsWithPnl.length > 0;
    }
    if (activeTab === 'app') {
      return appPositionsWithPnl.length > 0;
    }
    return externalPositionsWithPnl.length > 0;
  }, [activeTab, appPositionsWithPnl.length, externalPositionsWithPnl.length, isPaperMode, paperPositionsWithPnl.length]);
  
  const shouldShowBlockingLoader = loading && !hasRenderablePositions;
  
  // Render position row
  const renderPositionRow = ({
    position,
    pnl
  }: { position: Position; pnl: PositionPnL }, idx: number) => {
    const entryDisplay = safeParseFloat(position.entryPrice ?? position.entry_price);
    const { baseQty: sizeValue } = resolvePositionAmounts(position, entryDisplay);
    const markBase = safeParseFloat(position.markPrice ?? position.mark_price ?? position.current_price);
    
    const symbolUpper = position.symbol?.toUpperCase() ?? '';
    const isBtc = symbolUpper === 'BTCUSDT' || symbolUpper.startsWith('BTC');
    const resolvedMark = isBtc && canonicalBtcMarkPrice ? canonicalBtcMarkPrice : (markBase ?? undefined);
    
    const isLongRow = isPositionActuallyLong(position);
    const isProfit = pnl.pnl >= 0;
    const leverageValue = safeParseFloat(position.leverage ?? (position as any).leverageMultiplier);
    const baseQtyDisplay = sizeValue !== undefined ? Math.abs(sizeValue) : undefined;
    const notionalMark = baseQtyDisplay !== undefined && resolvedMark !== undefined
      ? baseQtyDisplay * resolvedMark
      : undefined;
    
    const takeProfitTarget = safeParseFloat(position.take_profit ?? position.takeProfit, undefined);
    const stopLossTarget = safeParseFloat(position.stop_loss ?? position.stopLoss, undefined);
    
    const rowId = String(position.paper_trade_id ?? position.paperTradeId ?? (position as any).trade_id ?? (position as any).tradeId ?? position.id ?? '');
    const isRowClosing = !!rowId && closingPosition === rowId;
    const tpSlCapability = getTpSlCapability(position);
    
    return (
      <div key={idx}>
        {/* Desktop Row */}
        <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 text-sm hover:bg-muted/30 transition-colors">
          {/* Symbol Column */}
          <div className="col-span-2 flex items-center gap-2">
            <div className="flex flex-col">
              <div className="font-semibold">{position.symbol}</div>
              <div className="flex items-center gap-1">
                <span className={`px-1.5 py-0.5 text-xs rounded font-medium ${
                  isLongRow
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                }`}>
                  {isLongRow ? t('trading.long') : t('trading.short')}
                </span>
              </div>
            </div>
          </div>

          {/* Size Column */}
          <div className="col-span-1 text-center">
            <div className="font-medium">
              {baseQtyDisplay !== undefined ? formatNumber(baseQtyDisplay, 4) : '--'}
            </div>
            <div className="text-xs text-muted-foreground">
              {notionalMark !== undefined ? formatCurrency(notionalMark) : ''}
            </div>
          </div>

          {/* Entry Price Column */}
          <div className="col-span-1 text-center">
            <div className="font-medium">
              {entryDisplay !== undefined ? formatCurrency(entryDisplay) : '--'}
            </div>
          </div>

          {/* Mark Price Column */}
          <div className="col-span-1 text-center">
            <div className="font-medium">
              {resolvedMark !== undefined ? formatCurrency(resolvedMark) : '--'}
            </div>
          </div>

          {/* Liquidation Price Column */}
          <div className="col-span-1 text-center">
            <div className="font-medium text-orange-600">
              {position.liquidation_price !== null && position.liquidation_price !== undefined
                ? formatCurrency(Number(position.liquidation_price))
                : '--'}
            </div>
          </div>

          {/* Margin Column */}
          <div className="col-span-1 text-center">
            <div className="font-medium">
              {pnl.isolatedMargin !== undefined ? formatCurrency(pnl.isolatedMargin) : '--'}
            </div>
            <div className="text-xs text-muted-foreground">
              {isPaperMode && paperInvestmentPercentage !== null
                ? `${formatNumber(paperInvestmentPercentage, 2)}%`
                : leverageValue !== undefined && leverageValue !== null
                  ? `${formatNumber(Number(leverageValue), 2)}x`
                  : ''}
            </div>
          </div>

          {/* PnL Column */}
          <div className="col-span-1 text-center">
            <div className={`font-bold ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
              {Number.isFinite(pnl.pnl) ? `${isProfit ? '+' : ''}${formatNumber(pnl.pnl, 2)}` : '--'}
            </div>
            <div className="text-[11px] text-muted-foreground">
              {Number.isFinite(pnl.percentage) ? `${isProfit ? '+' : ''}${formatNumber(pnl.percentage, 4)}% ROI` : '--'}
            </div>
          </div>

          {/* TP/SL Column */}
          <div className="col-span-1 text-center">
            <div className="text-xs space-y-1">
              <div className="text-green-600">
                TP: {takeProfitTarget !== undefined ? formatCurrency(takeProfitTarget) : '--'}
              </div>
              <div className="text-red-600">
                SL: {stopLossTarget !== undefined ? formatCurrency(stopLossTarget) : '--'}
              </div>
            </div>
          </div>

          {/* Actions Column */}
          <div className="col-span-3 flex items-center justify-center gap-1">
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-xs"
              onClick={() => handleOpenCloseConfirmModal(position)}
              disabled={isRowClosing}
            >
              {isRowClosing ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-current mr-1" />
                  Closing...
                </>
              ) : (
                t('dashboard.activePositions.close')
              )}
            </Button>

            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-xs"
              type="button"
              onClick={() => handleOpenTpSlModal(position)}
              disabled={isRowClosing || tpSlCapability === 'read_only'}
              title={tpSlCapability === 'read_only' ? 'TP/SL editing is unavailable for this external position' : undefined}
            >
              {t('trading.editTpSl')}
            </Button>
          </div>
        </div>

        {/* Mobile Card */}
        <div className="md:hidden p-4 hover:bg-muted/30 transition-colors">
          <div className="space-y-3">
            {/* Mobile Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-base">{position.symbol}</span>
                <span className={`px-2 py-1 text-xs rounded font-medium ${
                  isLongRow
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                }`}>
                  {isLongRow ? t('trading.long') : t('trading.short')}
                </span>
              </div>
            </div>

            {/* Mobile Details Grid */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-muted-foreground text-xs">{t('dashboard.activePositions.headers.entryPrice')}</div>
                <div className="font-medium">{entryDisplay !== undefined ? formatCurrency(entryDisplay) : '--'}</div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">{t('dashboard.activePositions.headers.markPrice')}</div>
                <div className="font-medium">{resolvedMark !== undefined ? formatCurrency(resolvedMark) : '--'}</div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">{t('dashboard.activePositions.headers.size')}</div>
                <div className="font-medium">{baseQtyDisplay !== undefined ? formatNumber(baseQtyDisplay, 4) : '--'}</div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">{t('dashboard.activePositions.headers.pnlRoi')}</div>
                <div className={`font-semibold ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
                  {Number.isFinite(pnl.pnl) ? `${isProfit ? '+' : ''}${pnl.pnl.toFixed(2)} USDT` : '--'}
                </div>
              </div>
            </div>
            
            {/* Mobile Actions */}
            <div className="flex items-center gap-2 pt-2">
              <Button
                size="sm"
                variant="outline"
                className="flex-1 h-8 text-xs"
                onClick={() => handleOpenCloseConfirmModal(position)}
                disabled={isRowClosing}
              >
                {isRowClosing ? t('dashboard.activePositions.closing') : t('dashboard.activePositions.close')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="flex-1 h-8 text-xs"
                onClick={() => handleOpenTpSlModal(position)}
                disabled={isRowClosing || tpSlCapability === 'read_only'}
              >
                {t('trading.editTpSl')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Render positions list
  const positionIncidents = useMemo(() => {
    const diagnostics = positionsState.diagnostics as any;
    return Array.isArray(diagnostics?.position_incidents) ? diagnostics.position_incidents : [];
  }, [positionsState.diagnostics]);

  const exchangeSyncStatus = useMemo(() => {
    const diagnostics = positionsState.diagnostics as any;
    return diagnostics?.exchange_sync_status ?? { healthy: true, incident_count: 0, incidents: [] };
  }, [positionsState.diagnostics]);

  const renderPositions = (
    positionsWithPnlList: Array<{ position: Position; pnl: PositionPnL }>,
    _title: string,
    _source: string
  ) => {
    if (!positionsWithPnlList || positionsWithPnlList.length === 0) {
      return (
        <div className="text-muted-foreground text-center py-8">
          <p className="text-lg font-medium mb-2">{t('dashboard.activePositions.noPositions')}</p>
          <p className="text-sm">{t('dashboard.activePositions.noPositionsDesc')}</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Section Header */}
        <div className="flex items-center justify-between">
          <h4 className={`font-medium text-sm flex items-center gap-2 ${_source === 'paper' ? 'text-orange-600 dark:text-orange-400' : 'text-muted-foreground'}`}>
            {_source === 'paper' ? t('trading.paperTradingPositions') : _title}
            <span className={`text-xs px-2 py-1 rounded ${_source === 'paper' ? 'bg-orange-100 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400' : 'bg-muted'}`}>
              {`${positionsWithPnlList.length} ${positionsWithPnlList.length === 1 ? t('dashboard.activePositions.position') : t('dashboard.activePositions.positions')}`}
            </span>
          </h4>
          <div className={`text-xs ${_source === 'paper' ? 'text-orange-500 dark:text-orange-400' : 'text-muted-foreground'}`}>
            {_source === 'paper' ? t('trading.paperTradingMode') : `CEX: ${_source === 'app' ? t('trading.internal') : t('trading.external')}`}
          </div>
        </div>

        {/* Modern Table Design */}
        <div className="bg-card border border-border rounded-lg overflow-hidden shadow-sm">
          {/* Desktop Table Header */}
          <div className="hidden md:block bg-muted/50 border-b border-border">
            <div className="grid grid-cols-12 gap-2 px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
              <div className="col-span-2">{t('dashboard.activePositions.headers.symbol')}</div>
              <div className="col-span-1 text-center">{t('dashboard.activePositions.headers.size')}</div>
              <div className="col-span-1 text-center">{t('dashboard.activePositions.headers.entryPrice')}</div>
              <div className="col-span-1 text-center">{t('dashboard.activePositions.headers.markPrice')}</div>
              <div className="col-span-1 text-center">{t('dashboard.activePositions.headers.liqPrice')}</div>
              <div className="col-span-1 text-center">{t('dashboard.activePositions.headers.margin')}</div>
              <div className="col-span-1 text-center">{t('dashboard.activePositions.headers.pnlRoi')}</div>
              <div className="col-span-1 text-center whitespace-nowrap">{t('dashboard.activePositions.headers.tpSlProjection')}</div>
              <div className="col-span-3 text-center">{t('dashboard.activePositions.headers.actions')}</div>
            </div>
          </div>

          {/* Mobile Table Header */}
          <div className="md:hidden bg-muted/50 border-b border-border px-4 py-3">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Active Positions
            </div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-border">
            {positionsWithPnlList.map(renderPositionRow)}
          </div>
        </div>
      </div>
    );
  };
  
  return (
    <>
      <Card className={`${isMobile ? 'mb-4 w-full min-w-0' : 'mb-6 w-full'} max-w-full overflow-hidden`}>
        <CardContent className={`${isMobile ? 'p-3' : 'p-6'} flex flex-col gap-3`}>
          {/* Header */}
          <div className={`flex ${isMobile ? 'flex-col gap-2' : 'justify-between items-center'} mb-2`}>
            <div className="flex items-center gap-2">
              <h3 className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium ${isPaperMode ? 'text-orange-600 dark:text-orange-400' : 'text-muted-foreground'}`}>
                {t('dashboard.activePositions.title')}
              </h3>
              {isPaperMode && (
                <span className="text-xs bg-orange-100 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 px-2 py-1 rounded">
                  {t('trading.paperTradingLabel')}
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {!isPaperMode && (
                <div className="flex border rounded">
                  {([['all', 'All'], ['app', 'App Signals'], ['external', 'External (CEX)']] as const).map(([tab, label]) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-3 py-1 text-xs ${tab === 'app' ? 'font-medium' : ''} ${
                        activeTab === tab
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background hover:bg-accent'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => fetchPositions(activeTab, { forceRefresh: true })}
                disabled={loading}
                aria-label="Refresh Active Positions"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                {t('dashboard.activePositions.refresh')}
              </Button>
            </div>
          </div>

          {/* Error message */}
          {error && hasRenderablePositions && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
              {error}
            </div>
          )}

          {exposureWarning && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
              <p className="font-medium">{exposureWarning.title}</p>
              <p>{exposureWarning.description}</p>
            </div>
          )}

          {!isPaperMode && !exchangeSyncStatus.healthy && positionIncidents.length > 0 && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              <p className="font-medium">Exchange state differs from the app state</p>
              <p>
                DeepTrade detected {positionIncidents.length} live position incident{positionIncidents.length === 1 ? '' : 's'} while syncing with your exchange.
              </p>
              <p className="mt-1 text-xs">
                {positionIncidents
                  .slice(0, 3)
                  .map((incident: any) => `${incident.kind}:${incident.symbol}:${incident.side}`)
                  .join(' | ')}
              </p>
            </div>
          )}

          {/* Content */}
          {shouldShowBlockingLoader ? (
            <div className="flex items-center justify-center h-24">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : error && !hasRenderablePositions ? (
            <div className="text-destructive text-center py-4">
              <p className="font-medium">{t('dashboard.activePositions.errorLoading')}</p>
              <p className="text-xs">{error}</p>
              <Button
                size="sm"
                variant="outline"
                className="mt-2"
                onClick={() => fetchPositions(activeTab, { forceRefresh: true })}
              >
                {t('dashboard.activePositions.retry')}
              </Button>
            </div>
          ) : isPaperMode ? (
            // Paper mode positions
            <div className="space-y-6">
              {positions && positions.length > 0 ? (
                renderPositions(paperPositionsWithPnl, t('trading.paperTradingPositions'), "paper")
              ) : (
                <div className="text-center py-12">
                  <div className="text-muted-foreground mb-4">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center">
                      <svg className="w-8 h-8 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <p className="text-lg font-medium mb-2 text-orange-600 dark:text-orange-400">{t('trading.noPaperPositions')}</p>
                    <p className="text-sm text-orange-500 dark:text-orange-400 max-w-md mx-auto">
                      {t('trading.enableAutoTradingPaper')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ) : activeTab === 'all' ? (
            // Live mode - All positions
            <div className="space-y-6">
              {positionsState.app_positions && positionsState.app_positions.length > 0 &&
                renderPositions(appPositionsWithPnl, t('dashboard.activePositions.positionTypes.appPositions'), "app")}
              {positionsState.external_positions && positionsState.external_positions.length > 0 &&
                renderPositions(externalPositionsWithPnl, t('dashboard.activePositions.positionTypes.externalPositions'), "external")}
              {(!positionsState.app_positions || positionsState.app_positions.length === 0) &&
               (!positionsState.external_positions || positionsState.external_positions.length === 0) && (
                <div className="text-center py-12">
                  <div className="text-muted-foreground mb-4">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <p className="text-lg font-medium mb-2">{t('trading.youHaveNoPositions')}</p>
                    <p className="text-sm text-muted-foreground max-w-md mx-auto">
                      {t('trading.yourActivePositionsWillAppearHere')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ) : positions && positions.length > 0 ? (
            renderPositions(
              activeTab === 'app' ? appPositionsWithPnl :
              activeTab === 'external' ? externalPositionsWithPnl :
              paperPositionsWithPnl,
              activeTab === 'app' ? t('dashboard.activePositions.positionTypes.appPositions') :
              activeTab === 'external' ? t('dashboard.activePositions.positionTypes.externalPositions') :
              t('dashboard.activePositions.allPositions'),
              activeTab
            )
          ) : (
            <div className="text-center py-12">
              <div className="text-muted-foreground mb-4">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <p className="text-lg font-medium mb-2">{t('trading.youHaveNoPosition')}</p>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  {activeTab === 'app' ? t('trading.noActiveAppPositions') :
                   activeTab === 'external' ? t('trading.noActiveExternalPositions') :
                   t('trading.yourActivePositionsWillAppear')}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* TP/SL Modal */}
      <TakeProfitStopLossModal
        isOpen={tpSlModalOpen}
        onClose={() => {
          setTpSlModalOpen(false);
          setTimeout(() => {
            setSelectedPosition(null);
            setNormalizedTpSlPosition(null);
          }, 50);
        }}
        onSetTakeProfit={handleSetTakeProfit}
        onSetStopLoss={handleSetStopLoss}
        currentPrice={normalizedTpSlPosition?.markPrice}
        position={selectedPosition ? {
          symbol: selectedPosition.symbol || '',
          side: selectedPosition.side || '',
          entryPrice: safeParseFloat(selectedPosition.entryPrice ?? selectedPosition.entry_price) || 0,
          size: safeParseFloat(selectedPosition.size ?? selectedPosition.quantity) || 0,
          isolatedMargin: calculatePositionPNL(selectedPosition).isolatedMargin,
          leverage: safeParseFloat((selectedPosition as any)?.leverage ?? (selectedPosition as any)?.leverageMultiplier),
          isLong: isPositionActuallyLong(selectedPosition),
          markPrice: canonicalBtcMarkPrice || undefined,
        } : undefined}
        mode={isPaperMode ? 'paper' : 'live'}
      />

      {/* Close Confirmation Modal */}
      <ConfirmationModal
        isOpen={isCloseConfirmModalOpen}
        onClose={handleCloseConfirmModal}
        onConfirm={handleConfirmClosePosition}
        title={t('dashboard.activePositions.closePosition')}
        description={t('dashboard.activePositions.closePositionConfirmDesc')}
        confirmText={t('dashboard.activePositions.closePosition')}
        cancelText={t('common.cancel')}
        variant="danger"
        isLoading={closingPosition === String(positionToClose?.paper_trade_id ?? positionToClose?.paperTradeId ?? positionToClose?.trade_id ?? positionToClose?.tradeId ?? positionToClose?.id ?? '')}
      >
        {positionToClose && (
          <div className="mt-4 p-3 bg-muted/50 rounded-lg">
            <div className="text-sm space-y-2">
              <div className="flex justify-between">
                <span className="font-medium">{t('dashboard.activePositions.headers.symbol')}:</span>
                <span>{positionToClose.symbol}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">{t('dashboard.activePositions.headers.size')}:</span>
                <span>{positionToClose.size || positionToClose.quantity}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">PnL:</span>
                <span className={`font-bold ${(() => {
                  const dynamicPnl = calculatePositionPNL(positionToClose);
                  return dynamicPnl.pnl >= 0 ? 'text-green-600' : 'text-red-600';
                })()}`}>
                  {(() => {
                    const dynamicPnl = calculatePositionPNL(positionToClose);
                    return dynamicPnl.pnl !== 0
                      ? `${dynamicPnl.pnl >= 0 ? '+' : ''}${formatNumber(dynamicPnl.pnl, 2)} USDT`
                      : '--';
                  })()}
                </span>
              </div>
            </div>
          </div>
        )}
      </ConfirmationModal>
    </>
  );
});

export default ActivePosition;
